import { SecurityHelper } from './helpers.js';

const CONFIG_FETCH_TIMEOUT_MS = 5000;
const CONFIG_FETCH_RETRIES = 2;

export const configKeys = [
  'webhookUrl',
  'primaryColor',
  'secondaryColor',
  'inputBorderColor',
  'chatbotName',
  'welcomeMessage',
  'bubbleIcon',
  'bubbleBackground',
  'sendIcon',
  'botAvatar',
  'userAvatar',
  'suggestions',
  'privacyPolicyUrl',
  'showBranding',
  'sendButtonColor',
  'lang',
  'theme'
];

export const defaultConfig = {
  webhookUrl: '',
  primaryColor: '#7B40F1',
  secondaryColor: '#f9f9f9',
  inputBorderColor: '#ddd',
  chatbotName: 'Chatyx',
  welcomeMessage: 'Bonjour, comment puis-je vous aider ?',
  bubbleIcon: 'https://cdn-icons-png.flaticon.com/512/4712/4712027.png',
  bubbleBackground: '#fff',
  sendIcon: 'https://cdn-icons-png.flaticon.com/512/2951/2951226.png',
  botAvatar: 'https://cdn-icons-png.flaticon.com/512/3940/3940056.png',
  userAvatar: 'https://cdn-icons-png.flaticon.com/512/4140/4140048.png',
  suggestions: [],
  privacyPolicyUrl: 'https://chatyx.fr/politique-de-confidentialite/',
  showBranding: true,
  sendButtonColor: null,
  lang: 'fr',
  theme: 'light'
};

export function fetchBotConfig(botId, apiBaseUrl) {
  if (!botId) return Promise.resolve({});
  const url = `${apiBaseUrl}/chatbot-widget-config?botId=${encodeURIComponent(botId)}`;

  const fetchWithTimeout = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS);
    try {
      return await fetch(url, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  return (async () => {
    for (let attempt = 0; attempt <= CONFIG_FETCH_RETRIES; attempt++) {
      try {
        const resp = await fetchWithTimeout();
        if (!resp.ok) return {};
        return await resp.json();
      } catch (_) {
        if (attempt === CONFIG_FETCH_RETRIES) return {};
      }
    }
    return {};
  })();
}

function isEmpty(value) {
  return value === undefined || value === null || value === '';
}

// Some remote payloads carry the bot name under a different key. Normalise
// them onto `chatbotName` before the merge so precedence stays predictable.
function applyChatbotNameAliases(source) {
  if (source.botTitle && !source.chatbotName) source.chatbotName = source.botTitle;
  if (source.botName && !source.chatbotName) source.chatbotName = source.botName;
  if (source.name && !source.chatbotName) source.chatbotName = source.name;
}

// Per-key coercion of string inputs (data attributes are always strings).
function coerceConfigValue(key, value) {
  if (key === 'suggestions' && typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value.split(';');
    }
  }
  if (key === 'showBranding' && typeof value === 'string') {
    return value === 'true' || value === '1';
  }
  return value;
}

// Apply the send-button default, validation and sanitization to a merged
// config object. Shared by every config source (script dataset, remote
// fetch, plain object) so the embedded preview renders exactly like prod.
function finalizeConfig(config) {
  if (!config.sendButtonColor) {
    config.sendButtonColor = config.primaryColor;
  }

  const supportedLangs = ['fr', 'en'];
  if (!supportedLangs.includes(config.lang)) {
    config.lang = defaultConfig.lang;
  }

  const supportedThemes = ['auto', 'light', 'dark'];
  if (!supportedThemes.includes(config.theme)) {
    config.theme = defaultConfig.theme;
  }

  config.bubbleIcon =
    SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(config.bubbleIcon)) || defaultConfig.bubbleIcon;
  config.botAvatar =
    SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(config.botAvatar)) || defaultConfig.botAvatar;
  config.userAvatar =
    SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(config.userAvatar)) || defaultConfig.userAvatar;
  config.sendIcon =
    SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(config.sendIcon)) || defaultConfig.sendIcon;
  config.privacyPolicyUrl =
    SecurityHelper.escapeAttribute(SecurityHelper.sanitizeURL(config.privacyPolicyUrl)) ||
    defaultConfig.privacyPolicyUrl;
  config.chatbotName = SecurityHelper.escapeHTML(config.chatbotName || defaultConfig.chatbotName);
  config.welcomeMessage = SecurityHelper.sanitizeHTML(config.welcomeMessage || defaultConfig.welcomeMessage);

  return config;
}

// Build a config from the embed <script> data attributes + a remote payload.
// Precedence: remote config > data attribute > default.
export function buildConfig(scriptElement, remoteConfig) {
  const remote = { ...(remoteConfig || {}) };
  applyChatbotNameAliases(remote);

  const dataset = (scriptElement && scriptElement.dataset) || {};
  const config = {};
  configKeys.forEach((key) => {
    const dataVal = dataset[key.replace(/-([a-z])/g, (g) => g[1].toUpperCase())];
    let value = remote[key];
    if (isEmpty(value)) {
      value = dataVal;
      if (isEmpty(value)) value = defaultConfig[key];
    }
    config[key] = coerceConfigValue(key, value);
  });

  return finalizeConfig(config);
}

// Build a config from a plain object (used for programmatic/embedded mounting,
// e.g. the live dashboard preview). Precedence: provided value > default.
export function buildConfigFromObject(source) {
  const src = { ...(source || {}) };
  applyChatbotNameAliases(src);

  const config = {};
  configKeys.forEach((key) => {
    let value = src[key];
    if (isEmpty(value)) value = defaultConfig[key];
    config[key] = coerceConfigValue(key, value);
  });

  return finalizeConfig(config);
}
